import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { LANGS, useI18n } from "@/lib/i18n";
import { Card } from "./BookingsTab";

const AMENITY_KEYS = ["beach_access", "wifi", "ac", "kitchen", "coffee_maker", "oven", "washer_dryer", "smart_tv", "parking", "hammock", "outdoor_shower", "garden", "outdoor_dining", "books_games"];
const TEXT_FIELDS = [["title", "Título", false], ["tagline", "Frase corta", false], ["description", "Descripción", true], ["neighborhood", "El vecindario", true]];
const NUMS = [["price_per_night", "Precio por noche (USD)"], ["cleaning_fee", "Tarifa de limpieza (USD)"], ["min_nights", "Noches mínimas"], ["max_guests", "Huéspedes máx."], ["bedrooms", "Habitaciones"], ["beds", "Camas"], ["baths", "Baños"], ["rating", "Calificación"], ["reviews_count", "Nº reseñas"]];

export const Field = ({ label, children }) => <label className="block space-y-1.5"><span className="text-xs font-semibold uppercase tracking-wider text-[#4A3525]">{label}</span>{children}</label>;

export const ListingTab = ({ prop, onSaved }) => {
  const { t } = useI18n();
  const [d, setD] = useState(() => JSON.parse(JSON.stringify(prop)));
  const [lang, setLang] = useState("es");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));
  const setNested = (k, sub, v) => setD((x) => ({ ...x, [k]: { ...(x[k] || {}), [sub]: v } }));
  const toggleAm = (k) => set("amenities", d.amenities.includes(k) ? d.amenities.filter((a) => a !== k) : [...d.amenities, k]);
  const save = async () => {
    setBusy(true);
    const body = { ...d };
    NUMS.forEach(([k]) => (body[k] = Number(body[k] || 0)));
    ["id", "slug", "photos", "reviews", "ical_token", "last_synced_at", "last_sync_status", "created_at", "ical_import_url"].forEach((k) => delete body[k]);
    try {
      const { data } = await api.put(`/admin/properties/${prop.id}`, body);
      onSaved(data);
      toast.success("Cambios guardados y publicados");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-12" data-testid="admin-listing">
      <div className="space-y-6 lg:col-span-7">
        <Card className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="font-display text-2xl">Textos del anuncio</p>
            <div className="flex gap-1 rounded-full bg-[#F3EFE6] p-1">
              {LANGS.map((l) => <button key={l.code} data-testid={`listing-lang-${l.code}`} onClick={() => setLang(l.code)} className={`rounded-full px-3 py-1 text-xs font-semibold ${lang === l.code ? "bg-[#2C2016] text-white" : ""}`}>{l.short}</button>)}
            </div>
          </div>
          <Field label="Nombre de la unidad"><Input data-testid="listing-name-input" value={d.name} onChange={(e) => set("name", e.target.value)} /></Field>
          {TEXT_FIELDS.map(([k, label, long]) => (
            <Field key={k} label={`${label} (${lang.toUpperCase()})`}>
              {long
                ? <Textarea data-testid={`listing-${k}-input`} className="min-h-[160px]" value={d[k]?.[lang] || ""} onChange={(e) => setNested(k, lang, e.target.value)} />
                : <Input data-testid={`listing-${k}-input`} value={d[k]?.[lang] || ""} onChange={(e) => setNested(k, lang, e.target.value)} />}
            </Field>
          ))}
        </Card>
        <Card>
          <p className="font-display text-2xl">Servicios</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {AMENITY_KEYS.map((k) => (
              <label key={k} className="flex items-center gap-3 text-sm"><Checkbox data-testid={`amenity-check-${k}`} checked={d.amenities.includes(k)} onCheckedChange={() => toggleAm(k)} />{t(`am.${k}`)}</label>
            ))}
          </div>
        </Card>
      </div>
      <div className="space-y-6 lg:col-span-5">
        <Card className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="font-display text-2xl">Precio y capacidad</p>
            <label className="flex items-center gap-2 text-sm">Color agenda <input data-testid="listing-color-input" type="color" value={d.color || "#16A34A"} onChange={(e) => set("color", e.target.value)} className="h-8 w-10 cursor-pointer" /></label>
            <label className="flex items-center gap-2 text-sm">Publicada <Switch data-testid="listing-active-switch" checked={!!d.active} onCheckedChange={(v) => set("active", v)} /></label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {NUMS.map(([k, label]) => (
              <Field key={k} label={label}><Input data-testid={k === "price_per_night" ? "admin-price-input" : `listing-${k}-input`} type="number" step="any" min="0" value={d[k] ?? ""} onChange={(e) => set(k, e.target.value)} /></Field>
            ))}
            <Field label="Llegada desde"><Input value={d.checkin_from} onChange={(e) => set("checkin_from", e.target.value)} /></Field>
            <Field label="Llegada hasta"><Input value={d.checkin_to} onChange={(e) => set("checkin_to", e.target.value)} /></Field>
            <Field label="Salida antes de"><Input value={d.checkout_by} onChange={(e) => set("checkout_by", e.target.value)} /></Field>
          </div>
        </Card>
        <Card className="space-y-3">
          <p className="font-display text-2xl">Anfitrión y contacto</p>
          <Field label="Nombre anfitrión"><Input value={d.host?.name || ""} onChange={(e) => setNested("host", "name", e.target.value)} /></Field>
          <Field label="Años como anfitrión"><Input type="number" value={d.host?.years ?? 0} onChange={(e) => setNested("host", "years", Number(e.target.value))} /></Field>
          <Field label="WhatsApp (con código de país)"><Input data-testid="listing-whatsapp-input" placeholder="+506 ..." value={d.contact?.whatsapp || ""} onChange={(e) => setNested("contact", "whatsapp", e.target.value)} /></Field>
          <Field label="Correo de contacto"><Input data-testid="listing-email-input" value={d.contact?.email || ""} onChange={(e) => setNested("contact", "email", e.target.value)} /></Field>
          <Field label="Ubicación (texto)"><Input value={d.location?.area || ""} onChange={(e) => setNested("location", "area", e.target.value)} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Latitud del mapa"><Input data-testid="listing-lat-input" type="number" step="0.00001" value={d.location?.lat ?? ""} onChange={(e) => setNested("location", "lat", Number(e.target.value))} /></Field>
            <Field label="Longitud del mapa"><Input data-testid="listing-lng-input" type="number" step="0.00001" value={d.location?.lng ?? ""} onChange={(e) => setNested("location", "lng", Number(e.target.value))} /></Field>
          </div>
          <p className="text-xs text-[#4A3525]">Para ajustar el punto: en Google Maps haz clic derecho sobre la casa y copia los dos números (latitud, longitud).</p>
          <Field label="Enlace del anuncio en Airbnb"><Input value={d.airbnb_url || ""} onChange={(e) => set("airbnb_url", e.target.value)} /></Field>
        </Card>
        <button data-testid="admin-save-price-button" disabled={busy} onClick={save} className="w-full rounded-xl bg-[#B8532F] py-4 font-semibold text-white disabled:opacity-60">{busy ? "Guardando…" : "Guardar cambios"}</button>
      </div>
    </div>
  );
};
